import React, { useState, useEffect, useCallback } from 'react';
import { grafanaAPI } from '../utils/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, BarChart, Bar
} from 'recharts';
import './MetricsDashboard.css';

// Time range options
const TIME_RANGES = [
  { label: '5m', value: '5m' },
  { label: '15m', value: '15m' },
  { label: '1h', value: '1h' },
  { label: '6h', value: '6h' },
  { label: '24h', value: '24h' },
  { label: '7d', value: '7d' },
];

// Chart colors
const CHART_COLORS = {
  primary: '#6366f1',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
};

function MetricsDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [panelData, setPanelData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collectorStatus, setCollectorStatus] = useState(null);
  const [timeRange, setTimeRange] = useState('1h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Fetch dashboard and panel data
  const fetchDashboard = useCallback(async () => {
    try {
      const dashboardData = await grafanaAPI.getDefaultDashboard();
      setDashboard(dashboardData);
      return dashboardData;
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
      throw err;
    }
  }, []);

  const fetchPanelData = useCallback(async (panels, duration) => {
    const dataPromises = panels.map(async (panel) => {
      try {
        const result = await grafanaAPI.queryMetrics(
          panel.metric_name,
          duration,
          panel.aggregation
        );
        return { panelId: panel.id, data: result };
      } catch (err) {
        console.error(`Failed to fetch data for panel ${panel.id}:`, err);
        return { panelId: panel.id, data: { data: [], error: err.message } };
      }
    });

    const results = await Promise.all(dataPromises);
    const newPanelData = {};
    results.forEach(({ panelId, data }) => {
      newPanelData[panelId] = data;
    });
    setPanelData(newPanelData);
  }, []);

  const fetchCollectorStatus = useCallback(async () => {
    try {
      const status = await grafanaAPI.getCollectorStatus();
      setCollectorStatus(status);
    } catch (err) {
      console.error('Failed to fetch collector status:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const dashboardData = await fetchDashboard();
      if (dashboardData?.panels?.length > 0) {
        await fetchPanelData(dashboardData.panels, timeRange);
      }
      await fetchCollectorStatus();
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [fetchDashboard, fetchPanelData, fetchCollectorStatus, timeRange]);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      if (dashboard?.panels?.length > 0) {
        fetchPanelData(dashboard.panels, timeRange);
        fetchCollectorStatus();
        setLastUpdate(new Date());
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, dashboard, timeRange, fetchPanelData, fetchCollectorStatus]);

  // Handle time range change
  const handleTimeRangeChange = (newRange) => {
    setTimeRange(newRange);
    if (dashboard?.panels?.length > 0) {
      fetchPanelData(dashboard.panels, newRange);
    }
  };

  // Manual refresh
  const handleRefresh = async () => {
    if (dashboard?.panels?.length > 0) {
      await fetchPanelData(dashboard.panels, timeRange);
      await fetchCollectorStatus();
      setLastUpdate(new Date());
    }
  };

  // Trigger manual collection
  const handleTriggerCollection = async () => {
    try {
      await grafanaAPI.triggerCollection();
      await handleRefresh();
    } catch (err) {
      console.error('Failed to trigger collection:', err);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Loading metrics dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="metrics-dashboard">
        <div className="page-header">
          <h1 className="page-title">Metrics Dashboard</h1>
        </div>
        <div className="error-state">
          <div className="error-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h3>Unable to connect to QuorumGrafana</h3>
          <p>{error}</p>
          <p className="hint">Make sure QuorumGrafana backend is running on port 8002</p>
          <button className="btn btn-primary" onClick={loadData}>
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="metrics-dashboard">
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">{dashboard?.name || 'Metrics Dashboard'}</h1>
          <p className="page-subtitle">{dashboard?.description || 'Real-time metrics visualization'}</p>
        </div>
        <div className="header-controls">
          {/* Time Range Selector */}
          <div className="time-range-selector">
            {TIME_RANGES.map(({ label, value }) => (
              <button
                key={value}
                className={`time-btn ${timeRange === value ? 'active' : ''}`}
                onClick={() => handleTimeRangeChange(value)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Auto Refresh Toggle */}
          <div className="refresh-controls">
            <label className="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <span>Auto-refresh</span>
            </label>
            <button className="btn btn-secondary btn-sm" onClick={handleRefresh}>
              <RefreshIcon />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Collector Status Bar */}
      <div className="collector-status">
        <div className="status-item">
          <span className={`status-dot ${collectorStatus?.running ? 'running' : 'stopped'}`}></span>
          <span>Collector: {collectorStatus?.running ? 'Running' : 'Stopped'}</span>
        </div>
        <div className="status-item">
          <span>Collections: {collectorStatus?.collection_count || 0}</span>
        </div>
        <div className="status-item">
          <span>Last: {collectorStatus?.last_collection ? new Date(collectorStatus.last_collection).toLocaleTimeString() : 'Never'}</span>
        </div>
        {lastUpdate && (
          <div className="status-item">
            <span>Updated: {lastUpdate.toLocaleTimeString()}</span>
          </div>
        )}
        <button className="btn btn-link btn-sm" onClick={handleTriggerCollection}>
          Collect Now
        </button>
      </div>

      {/* Panels Grid */}
      <div className="panels-grid">
        {dashboard?.panels?.map((panel) => (
          <MetricPanel
            key={panel.id}
            panel={panel}
            data={panelData[panel.id]}
            timeRange={timeRange}
          />
        ))}

        {(!dashboard?.panels || dashboard.panels.length === 0) && (
          <div className="no-panels">
            <p>No panels configured. Metrics will appear here once data is collected.</p>
            <button className="btn btn-primary" onClick={handleTriggerCollection}>
              Start Collecting Metrics
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Panel component
function MetricPanel({ panel, data, timeRange }) {
  const chartData = data?.data || [];
  const hasData = chartData.length > 0;

  // Format timestamp for display
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    if (timeRange === '7d') {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else if (timeRange === '24h' || timeRange === '6h') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  // Get chart color based on panel type
  const getChartColor = () => {
    if (panel.metric_name?.includes('error')) return CHART_COLORS.danger;
    if (panel.metric_name?.includes('latency') || panel.metric_name?.includes('duration')) return CHART_COLORS.warning;
    if (panel.metric_name?.includes('user')) return CHART_COLORS.success;
    return CHART_COLORS.primary;
  };

  // Render stat panel
  if (panel.panel_type === 'stat') {
    const currentValue = hasData ? chartData[chartData.length - 1]?.value : null;
    const formattedValue = currentValue !== null
      ? (panel.metric_name?.includes('duration') || panel.metric_name?.includes('latency'))
        ? `${currentValue.toFixed(2)}s`
        : currentValue.toLocaleString()
      : '--';

    return (
      <div className="metric-panel stat-panel">
        <div className="panel-header">
          <h3 className="panel-title">{panel.title}</h3>
        </div>
        <div className="stat-value" style={{ color: getChartColor() }}>
          {formattedValue}
        </div>
        <div className="stat-metric">{panel.metric_name}</div>
      </div>
    );
  }

  // Render gauge panel
  if (panel.panel_type === 'gauge') {
    const currentValue = hasData ? chartData[chartData.length - 1]?.value : 0;
    const maxValue = 100;
    const percentage = Math.min((currentValue / maxValue) * 100, 100);

    return (
      <div className="metric-panel gauge-panel">
        <div className="panel-header">
          <h3 className="panel-title">{panel.title}</h3>
        </div>
        <div className="gauge-container">
          <svg viewBox="0 0 100 60" className="gauge-svg">
            <path
              d="M10 50 A 40 40 0 0 1 90 50"
              fill="none"
              stroke="var(--border-color)"
              strokeWidth="8"
              strokeLinecap="round"
            />
            <path
              d="M10 50 A 40 40 0 0 1 90 50"
              fill="none"
              stroke={getChartColor()}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${percentage * 1.26} 126`}
            />
          </svg>
          <div className="gauge-value">{currentValue.toFixed(1)}</div>
        </div>
      </div>
    );
  }

  // Render line/area chart
  return (
    <div className={`metric-panel chart-panel ${panel.size?.width >= 6 ? 'wide' : ''}`}>
      <div className="panel-header">
        <h3 className="panel-title">{panel.title}</h3>
        <span className="panel-aggregation">{panel.aggregation}</span>
      </div>
      <div className="panel-chart">
        {hasData ? (
          <ResponsiveContainer width="100%" height={200}>
            {panel.panel_type === 'bar' ? (
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={formatTime}
                  stroke="var(--text-secondary)"
                  fontSize={11}
                />
                <YAxis stroke="var(--text-secondary)" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                  }}
                  labelFormatter={(label) => new Date(label).toLocaleString()}
                />
                <Bar dataKey="value" fill={getChartColor()} radius={[4, 4, 0, 0]} />
              </BarChart>
            ) : (
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id={`gradient-${panel.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={getChartColor()} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={getChartColor()} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={formatTime}
                  stroke="var(--text-secondary)"
                  fontSize={11}
                />
                <YAxis stroke="var(--text-secondary)" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                  }}
                  labelFormatter={(label) => new Date(label).toLocaleString()}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={getChartColor()}
                  fill={`url(#gradient-${panel.id})`}
                  strokeWidth={2}
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        ) : (
          <div className="no-data">
            <p>No data available</p>
            <span className="metric-name">{panel.metric_name}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Refresh icon component
function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
      <path d="M23 4v6h-6" />
      <path d="M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

export default MetricsDashboard;
